<script setup>
// Astra DS · AccountMenu — el panel de cuenta de la cabecera.
//
// Figma: `Pattern/AccountMenu 1291:456`, con los cuatro estados en
// `1292:5503`. La descripción del componente manda:
//
// «Panel de cuenta anclado a la cabecera web. Cuelga del filete de SiteHeader:
// sin borde superior, sin radio y sin sombra. USO · solo web, una instancia
// flotante en posición absoluta sobre la plantilla. El tema vive aquí, con
// Icon/moon e Icon/sun. NO · no lleva velo (no es modal), no es una segunda
// navegación y ninguna fila baja de 44.»
//
// ── 🔴 El tema se mudó aquí ──
//
// Hasta ahora el conmutador era un botón de contraste en la cabecera. El diseño
// lo trae dentro del panel, en dos celdas con luna y sol, y la cabecera se queda
// sin ese botón. En móvil el panel no existe, así que allí el tema sigue donde
// estaba: en `NavSheet`.
//
// ── Los cuatro estados salen de la sesión, no de una prop ──
//
// `anon` sin token; `oyente` con cuenta y sin perfil verificado; `artista` y
// `sello` cuando el perfil correspondiente está **verificado**. Una solicitud en
// revisión no cambia el estado del panel: enseña su estado y nada más, que es lo
// que pide el §4 del contrato.
//
// «Mis envíos», «Mi ficha» y «Mi roster» **ya llevan a sus pantallas**: entraron
// como filas inertes cuando no existían y dejaron de serlo al montarse las
// plantillas de área.
//
// Los contadores salen de `cuenta.envios` —que el servidor ya manda— contando la
// lista, y de `ficha` y `roster`, que todavía no llegan: hasta que lleguen se
// enseña el cero, que es lo acordado.
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCuentaStore } from "@/stores/cuenta";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import { useI18n } from "vue-i18n";
import { mesYAno } from "@/i18n/fechas";

const props = defineProps({
  open: { type: Boolean, default: false },

  // El ancho de la celda `head-end`, medido por la cabecera. Si es 0 —sin
  // `ResizeObserver`— vale el 296 del nodo de hoy.
  ancho: { type: Number, default: 0 },
  // `dark` | `light`. Lo sabe la aplicación, no este panel.
  theme: { type: String, default: "dark" },
});

const emit = defineEmits(["close", "toggle-theme"]);

const route = useRoute();
const router = useRouter();
const cuenta = useCuentaStore();
const { t } = useI18n();

// El perfil manda solo cuando está **verificado**. `sin-verificar-email`,
// `sin-material` y `pendiente` son solicitudes, y una solicitud no es una
// identidad: el panel sigue enseñando la cara de oyente hasta que la redacción
// aprueba. Por eso el estado nuevo del 01.09.26 no obliga a tocar nada aquí.
// Desde el 13.09.26 de `cuenta.roles`, que es lo que dice el servidor.
const estado = computed(() => {
  if (!cuenta.haySesion) return "anon";
  if (cuenta.esArtista) return "artista";
  if (cuenta.esSello) return "sello";
  return "oyente";
});

// ── La fila de perfil ──
//
// 🔴 **«Pedir perfil de artista» solo se enseña si NO hay solicitud.** Estaba
// atada a `estado === 'oyente'`, y como el panel considera oyente a todo el que
// no tiene perfil **verificado**, a quien ya lo había pedido le seguía ofreciendo
// pedirlo. Es lo primero del «qué NO hacer» del contrato de cuentas §4, añadido
// el 01.09.26 al verse en una cuenta real.
//
// Con solicitud hecha, la fila dice **en qué anda y cuál es el paso siguiente**,
// que es lo que el contrato pide para cada estado. Y el destino cambia con ella:
// de nada sirve decir «envía tu música» y llevar al mismo sitio de siempre.
//
// `descartado` vuelve a ofrecer pedirlo: la solicitud se acabó y se puede
// intentar de nuevo.
//
// 🔴 **Sin solicitud, no hay fila.** Desde el 12.09.26 el contrato de cuentas
// §4 no deja enseñar nada de artistas ni sellos a un oyente, ni en su área ni
// aquí: la única puerta es el enlace discreto de los ajustes del área. Antes
// esta fila decía «Pedir perfil de artista» a todo el que solo escucha.
const solicitud = computed(() => cuenta.perfilArtista || cuenta.perfilSello || null);

const filaPerfil = computed(() => {
  // 🔴 **Sin el correo verificado, la cuenta no está activa** (13.09.26), sea
  // oyente, artista o sello: la fila lleva al área, que solo enseña el aviso.
  if (cuenta.cuenta && !cuenta.correoVerificado) {
    return {
      etiqueta: t("area.menu.fila.verificaCorreo"),
      meta: t("area.menu.fila.cuentaSinActivar"),
      acento: true,
      to: "/cuenta/area",
    };
  }

  if (estado.value !== "oyente") return null;

  const s = solicitud.value;
  if (!s) return null;
  if (s.estado === "descartado") {
    return { etiqueta: t("area.menu.fila.pedirPerfil"), to: "/cuenta/area#perfil" };
  }

  if (s.estado === "sin-verificar-email") {
    return {
      etiqueta: t("area.menu.fila.verificaCorreo"),
      meta: t("area.menu.fila.sinVerificarCorreo"),
      acento: true,
      to: "/cuenta/area#perfil",
    };
  }

  // Solo artistas, y solo desde el 01.09.26: la solicitud espera al primer envío
  // porque la redacción no puede aprobar a alguien a quien no ha escuchado.
  if (s.estado === "sin-material") {
    return {
      etiqueta: t("area.menu.fila.enviaMusica"),
      meta: t("area.menu.fila.faltaMusica"),
      acento: true,
      to: "/cuenta/envios/nuevo",
    };
  }

  return { etiqueta: t("area.menu.fila.tuPerfil"), meta: t("area.menu.fila.enRevision"), to: "/cuenta/area#perfil" };
});

const perfil = computed(() =>
  estado.value === "artista"
    ? cuenta.perfilArtista
    : estado.value === "sello"
      ? cuenta.perfilSello
      : null,
);

// El nombre que encabeza el panel: el del perfil verificado si lo hay, el de la
// persona si lo dio, y el correo como último recurso. Nunca un hueco.
const titulo = computed(() => {
  if (perfil.value?.nombre) return perfil.value.nombre;
  const n = cuenta.cuenta?.nombre;
  if (n) return n;
  const email = cuenta.cuenta?.email || "";
  return email.split("@")[0] || t("area.menu.tuCuenta");
});

const correo = computed(() => cuenta.cuenta?.email || "");

// ── Los datos de las filas de artista y de sello ──
//
// Formato del nodo: para artista «1 EN PARRILLA» (lo que está sonando), para
// sello «4 · 2 EN PARRILLA» (lo mandado y lo que suena). Cada pieza aparece solo
// si el servidor la manda.
const numero = (v) => (Number.isFinite(v) ? v : 0);

// El servidor manda la lista, así que los dos números se cuentan de ella y no
// hace falta que los mande aparte. Si algún día vinieran contados, mandan ellos.
const envios = computed(() => {
  const e = cuenta.cuenta?.envios || {};
  const items = e.items || [];
  const enParrillaN = Number.isFinite(e.enParrilla)
    ? e.enParrilla
    : items.filter((i) => i.estado === "parrilla").length;
  const totalN = Number.isFinite(e.total) ? e.total : items.length;
  const enParrilla = t("area.menu.enParrilla", { n: enParrillaN });
  // El sello manda música de varios, así que cuenta lo enviado y lo que suena;
  // el artista solo lo que suena. Los dos formatos son los del nodo.
  return estado.value === "sello"
    ? t("area.menu.enviosSello", { total: totalN, enParrilla })
    : enParrilla;
});

// La ficha no se cuenta, se está o no se está. Sin dato, el equivalente al cero
// es decir que aún no hay.
//
// ⚠️ **Traducido, no crudo.** Salía el valor del contrato tal cual —«publicada»
// en minúscula, y «sin-ficha» con su guion— en una fila que lee una persona. Las
// mismas dos palabras que usa el raíl del área, `utils/railDeCuenta.js`.
const ficha = computed(() =>
  cuenta.cuenta?.ficha?.estado === "publicada"
    ? t("area.menu.fichaPublicada")
    : t("area.menu.fichaTodaviaNo"),
);

const roster = computed(() => {
  const n = numero(cuenta.cuenta?.roster?.artistas);
  return t("area.menu.rosterArtistas", { n }, n);
});

// «OYENTE DESDE MARZO DE 2026», del `createdAt` del contrato. Si la fecha no
// viene o no se entiende, se dice «OYENTE» a secas: mejor corto que inventado.
const desde = computed(() => {
  const d = new Date(cuenta.cuenta?.createdAt || "");
  if (Number.isNaN(d.getTime())) return t("area.menu.oyente");
  return t("area.menu.oyenteDesde", { fecha: mesYAno(d) });
});

// La cuenta se pide **al abrir el panel**, no al arrancar la web: quien solo
// viene a escuchar no tiene por qué pagar una llamada de más en cada carga. Con
// el token guardado y sin datos, el panel enseñaría un nombre vacío hasta que
// alguien entrase en el área.
watch(
  () => props.open,
  (abierto) => {
    if (abierto && cuenta.haySesion && !cuenta.cuenta) cuenta.cargar();
  },
);

function irA() {
  // Cualquier fila que navegue cierra el panel: dejarlo abierto sobre la página
  // nueva lo convierte en un trozo de interfaz olvidado.
  emit("close");
}

/**
 * 🔴 **Cerrar sesión también saca de donde estabas.**
 *
 * Solo olvidaba el token y cerraba el panel, así que quien cerraba sesión desde
 * su área **seguía viendo su área**: las vistas de cuenta comprueban la sesión al
 * montarse y ninguna se estaba remontando. Se veían los datos de la cuenta que
 * acababa de salir hasta recargar a mano.
 *
 * A la portada y no a `/cuenta`: cerrar sesión es irse, no volver a la puerta a
 * que te pidan la contraseña otra vez.
 */
function cerrarSesion() {
  cuenta.salir();
  emit("close");
  if (route.path.startsWith("/cuenta")) router.push("/");
}
</script>

<template>
  <!-- Sin velo y sin `role="dialog"`: no es modal. Es un panel que cuelga del
       filete y que se cierra al pulsar fuera, con Escape o al navegar. -->
  <div
    v-if="open"
    class="account-menu"
    :style="{ width: `${ancho || 296}px` }"
    role="menu"
    :aria-label="t('area.menu.tuCuenta')"
  >
    <!-- ── Identidad ── -->
    <div v-if="estado !== 'anon'" class="account-menu__id">
      <p
        class="account-menu__kicker"
        :class="{ 'is-ok': estado === 'artista' || estado === 'sello' }"
      >
        <template v-if="estado === 'artista'">{{ t("area.menu.artistaVerificado") }}</template>
        <template v-else-if="estado === 'sello'">{{ t("area.menu.selloVerificado") }}</template>
        <template v-else>{{ desde }}</template>
      </p>
      <p class="account-menu__nombre">{{ titulo }}</p>
      <p class="account-menu__correo">{{ correo }}</p>
    </div>

    <!-- ── Las filas ──
         🔴 **Cuatro clases, del nodo `1979:578` (13.09.26).** Identidad y rótulo
         no llevan icono; un **destino** sale del panel y lleva el chevron a la
         derecha; una **acción** ocurre aquí y lleva su glifo a la izquierda. El
         chevron solo aparece donde se sale: antes lo llevaban también cerrar
         sesión y todas las filas, y el panel se leía como cuatro puertas iguales.

         El estado de una fila —«1 en parrilla», «Cuenta sin activar»— va **encima**
         de su etiqueta, no apretado a su derecha, que partía «Verifica tu correo»
         en dos líneas. -->

    <!-- ── Anon ──
         `Entrar` se queda en acento: es a la vez destino y acción principal, la
         única excepción a la tabla. -->
    <template v-if="estado === 'anon'">
      <router-link class="account-menu__row account-menu__row--accent" to="/cuenta" @click="irA">
        <span class="account-menu__texto">
          <span class="account-menu__label">{{ t("area.menu.entrar") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>
      <router-link class="account-menu__row" to="/cuenta?crear=1" @click="irA">
        <span class="account-menu__texto">
          <span class="account-menu__label">{{ t("area.menu.crearCuenta") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>
    </template>

    <!-- ── Con sesión ── -->
    <template v-else>
      <router-link
        v-if="filaPerfil && !cuenta.correoVerificado"
        class="account-menu__row"
        :to="filaPerfil.to"
        @click="irA"
      >
        <span class="account-menu__texto">
          <span v-if="filaPerfil.meta" class="account-menu__meta">{{ filaPerfil.meta }}</span>
          <span class="account-menu__label">{{ filaPerfil.etiqueta }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>

      <!-- Artista y sello comparten «Mis envíos»; la segunda fila es la ficha de
           uno y el roster del otro. -->
      <router-link
        v-if="estado === 'artista' || estado === 'sello'"
        class="account-menu__row"
        to="/cuenta/envios"
        @click="irA"
      >
        <span class="account-menu__texto">
          <span class="account-menu__meta">{{ envios }}</span>
          <span class="account-menu__label">{{ t("area.menu.misEnvios") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>

      <router-link
        v-if="estado === 'artista'"
        class="account-menu__row"
        to="/cuenta/ficha"
        @click="irA"
      >
        <span class="account-menu__texto">
          <span class="account-menu__meta">{{ ficha }}</span>
          <span class="account-menu__label">{{ t("area.menu.miFicha") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>

      <router-link
        v-if="estado === 'sello'"
        class="account-menu__row"
        to="/cuenta/roster"
        @click="irA"
      >
        <span class="account-menu__texto">
          <span class="account-menu__meta">{{ roster }}</span>
          <span class="account-menu__label">{{ t("area.menu.miRoster") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>

      <router-link class="account-menu__row" to="/cuenta/area" @click="irA">
        <span class="account-menu__texto">
          <span class="account-menu__label">{{ t("area.menu.miCuenta") }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>

      <router-link
        v-if="filaPerfil && cuenta.correoVerificado"
        class="account-menu__row"
        :to="filaPerfil.to"
        @click="irA"
      >
        <span class="account-menu__texto">
          <span v-if="filaPerfil.meta" class="account-menu__meta">{{ filaPerfil.meta }}</span>
          <span class="account-menu__label">{{ filaPerfil.etiqueta }}</span>
        </span>
        <AstraIcon class="account-menu__chevron" name="chevron-forward" :size="14" />
      </router-link>
    </template>

    <!-- ── Tema ──
         Dos celdas dentro de un filete. La activa se marca por debajo con el
         rojo: el estado lo lleva la línea, no un relleno. -->
    <div class="account-menu__theme">
      <p class="account-menu__kicker">{{ t("area.menu.tema") }}</p>
      <div class="account-menu__cells">
        <button
          type="button"
          class="account-menu__cell"
          :class="{ 'is-on': theme === 'dark' }"
          :aria-pressed="theme === 'dark' ? 'true' : 'false'"
          :aria-label="t('area.menu.temaOscuro')"
          @click="theme !== 'dark' && emit('toggle-theme')"
        >
          <AstraIcon name="moon" :size="16" />
        </button>
        <button
          type="button"
          class="account-menu__cell"
          :class="{ 'is-on': theme === 'light' }"
          :aria-pressed="theme === 'light' ? 'true' : 'false'"
          :aria-label="t('area.menu.temaClaro')"
          @click="theme !== 'light' && emit('toggle-theme')"
        >
          <AstraIcon name="sun" :size="16" />
        </button>
      </div>
    </div>

    <!-- ── El pie del panel ── -->
    <p v-if="estado === 'anon'" class="account-menu__note">
      {{ t("area.menu.notaAnon") }}
    </p>

    <!-- **Cerrar sesión es una acción, no un destino**: no va a ninguna parte.
         Glifo a la izquierda, sin chevron, en `label/lg` y bajando al escalón de
         `bg/canvas`, que es la única separación que admite este panel. -->
    <div v-else class="account-menu__pie">
      <button type="button" class="account-menu__accion" @click="cerrarSesion">
        <AstraIcon name="log-out-outline" :size="16" />
        <span>{{ t("area.menu.cerrarSesion") }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// 🔴 **Sin bordes, y pegado al borde derecho.** Actualizado el 31.08.26: el panel
// ocupa la celda `head-end` —hoy 296, medida en vivo por la cabecera— y la
// separación con la
// página la hace el **escalón de fondo**, de `bg/canvas` a `bg/elevated`, que es
// uno de los dos recursos de profundidad que admite el sistema.
//
// Un contorno alrededor de un panel que ya está a otro nivel de fondo no añade
// información: añade una línea que hay que justificar contra la retícula de la
// cabecera, y no la justificaba —el filete izquierdo caía 4px por delante de la
// vertical que la cabecera traza en 990, y el derecho flotaba 14px por dentro—.
//
// La cabecera lleva `overflow: hidden`, así que el panel NO puede vivir dentro:
// se monta como hermano y se ancla con `fixed` a su altura.
.account-menu {
  position: fixed;
  top: 46px;
  right: 0;
  z-index: 90;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--color-bg-elevated);
  border: 0;
  border-radius: 0;
}

.account-menu__id {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 16px 16px 14px;
}

.account-menu__kicker {
  @include t.mono-micro;
  margin: 0;
  color: var(--color-text-secondary);
}

// Verde, y solo aquí: es el único sitio del sistema donde algo está verificado.
.account-menu__kicker.is-ok {
  color: var(--color-feedback-success);
}

// 🔴 **El nombre no es una fila, y en caja alta lo parecía.** `display/station`:
// 26 ExtraBold en **caja normal**, frente a las filas en 19 Bold y en caja alta.
// Con `display/item` iba a 21 Bold en mayúsculas y «Horas Muertas» se leía igual
// que «Mis envíos». Nodo `1979:578`, 13.09.26. No hay mixin de `station`: se
// escribe aquí con los valores del nodo.
.account-menu__nombre {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.625rem; // 26
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--color-text-primary);
}

.account-menu__correo {
  @include t.mono-micro;
  margin: 0;
  color: var(--color-text-secondary);
}

// ── Destino ──
// Relleno 12/16 y hueco 10, del nodo. La fila mide lo que su texto: 43 sin
// estado, 58 con él.
.account-menu__row {
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  border-radius: 0;
  background: none;
  color: var(--color-text-primary);
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--color-bg-subtle);
    outline: none;
  }
}

.account-menu__texto {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.account-menu__label {
  @include t.display-row-name;
  color: var(--color-text-primary);
}

.account-menu__row--accent .account-menu__label {
  color: var(--color-text-accent);
}

// El estado de la fila, encima de su etiqueta.
.account-menu__meta {
  @include t.mono-micro;
  color: var(--color-feedback-warning);
}

.account-menu__chevron {
  flex: none;
  color: var(--color-icon-secondary);
}

.account-menu__theme {
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px 16px;
}

// Sin recuadro: las dos celdas se reparten el ancho y lo único dibujado es el
// filete de acento bajo la activa, que es marca de selección, no borde.
.account-menu__cells {
  display: flex;
  align-items: stretch;
}

.account-menu__cell {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 14px 0;
  border: 0;
  // El hueco del filete de estado se reserva siempre, o la celda activa
  // mediría 2px más que la otra y las dos bailarían al cambiar de tema.
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: none;
  color: var(--color-icon-secondary);
  cursor: pointer;

  &.is-on {
    border-bottom-color: var(--color-border-accent);
    color: var(--color-icon-primary);
  }
}

// ── Acción: cerrar sesión ──
// Baja al escalón de `bg/canvas`. Sin filete, sin radio, sin sombra.
.account-menu__pie {
  background: var(--color-bg-canvas);
}

.account-menu__accion {
  @include t.label-lg;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  border-radius: 0;
  background: none;
  color: var(--color-text-secondary);
  text-align: left;
  text-transform: uppercase;
  cursor: pointer;

  :deep(svg) {
    flex: none;
    color: var(--color-icon-secondary);
  }

  &:hover,
  &:focus-visible {
    color: var(--color-text-primary);
    outline: none;
  }
}

.account-menu__note {
  @include t.mono-micro;
  margin: 0;
  padding: 12px 16px;
  color: var(--color-text-secondary);
}

// Solo web, dice el componente. En móvil el acceso vive en `NavSheet`, que ya
// es el sitio donde está la navegación y el tema.
@media (max-width: 880px) {
  .account-menu {
    display: none;
  }
}
</style>
